import WebhookEventEnum from "../../../../models/WebhookEventEnum";
import WebhookDeliveryStatusEnum from "../../../../models/WebhookDeliveryStatusEnum";
import IWebhookSummary from "../../../../models/IWebhookSummary";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";

/** Where the webhooks guide lives on the docs site. */
export const WEBHOOK_DOCS_URL = "https://docs.orderwerks.com/webhooks";

export interface IWebhookEventGroup {
    /** Translation key of the group title. */
    labelKey: string;
    /** True when the group label uses the business's customer word. */
    vendorWord?: boolean;
    events: WebhookEventEnum[];
}

/** The subscribable events as the sidebar lists them, grouped by resource. */
export const WEBHOOK_EVENT_GROUPS: IWebhookEventGroup[] = [
    {
        labelKey: "webhook_group_order",
        events: [
            WebhookEventEnum.ORDER_CREATED,
            WebhookEventEnum.ORDER_SUBMITTED,
            WebhookEventEnum.ORDER_STATE_CHANGED,
            WebhookEventEnum.ORDER_DELETED,
        ],
    },
    {
        labelKey: "webhook_group_quote",
        events: [WebhookEventEnum.QUOTE_CREATED, WebhookEventEnum.QUOTE_SENT, WebhookEventEnum.QUOTE_CONVERTED_TO_ORDER],
    },
    {
        labelKey: "webhook_group_shipment",
        events: [WebhookEventEnum.SHIPMENT_CREATED, WebhookEventEnum.SHIPMENT_VOIDED, WebhookEventEnum.SHIPMENT_DELETED],
    },
    {
        labelKey: "webhook_group_catalog_item",
        events: [WebhookEventEnum.CATALOG_ITEM_CREATED, WebhookEventEnum.CATALOG_ITEM_UPDATED, WebhookEventEnum.CATALOG_ITEM_DELETED],
    },
    {
        labelKey: "webhook_group_vendor",
        vendorWord: true,
        events: [WebhookEventEnum.VENDOR_CREATED, WebhookEventEnum.VENDOR_UPDATED, WebhookEventEnum.VENDOR_DELETED],
    },
    {
        labelKey: "webhook_group_vendor_source",
        events: [WebhookEventEnum.VENDOR_SOURCE_CREATED, WebhookEventEnum.VENDOR_SOURCE_UPDATED, WebhookEventEnum.VENDOR_SOURCE_DELETED],
    },
];

/** Translation key for an event type: `order.state_changed` becomes `webhook_event_order_state_changed`. */
export const webhookEventLabelKey = (event: string): string => `webhook_event_${String(event).replace(/\./g, "_")}`;

/** Translation key for a delivery status pill. */
export const deliveryStatusLabelKey = (status: WebhookDeliveryStatusEnum | string): string => `delivery_status_${status}`;

export type WebhookHealth = "active" | "paused" | "disabled";

/** Disabled by Orderwerks beats paused by the business; otherwise active. */
export const webhookHealth = (webhook: Pick<IWebhookSummary, "active" | "disabled_at">): WebhookHealth => {
    if (webhook.disabled_at) {
        return "disabled";
    }

    return webhook.active ? "active" : "paused";
};

/**
 * Endpoints whose own workflow triggers already fire on this step. With `workflowGuid`, a trigger
 * counts when it names that workflow or any workflow (null); without it (the editor does not know
 * which workflow it is editing) any trigger on the step counts.
 */
export const endpointsTriggeredAtStep = (
    webhooks: IWebhookSummary[],
    step: Pick<IOrder_Progress_State, "order_state_id">,
    workflowGuid?: string | null
): IWebhookSummary[] =>
    webhooks.filter((webhook) =>
        (webhook.workflow_triggers || []).some(
            (trigger) =>
                Number(trigger.order_state_id) === Number(step.order_state_id) &&
                (workflowGuid === undefined || !trigger.workflow_guid || trigger.workflow_guid === workflowGuid)
        )
    );

/** A URL the sidebar accepts before asking the server: https, no credentials, no fragment. */
export const looksLikeWebhookUrl = (value: string): boolean => {
    try {
        const url = new URL(value.trim());

        return url.protocol === "https:" && !url.username && !url.password && !url.hash;
    } catch {
        return false;
    }
};
